/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { Breadcrumb, Button, Card, Col, Descriptions, Empty, Popconfirm, Row, Space, Spin, Statistic, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetStateChangesQuery,
  useGetStateSnapshotsQuery,
  useGetSystemMetricsQuery,
  type AdminActiveSessionMetrics,
} from '../../api/adminApi';
import { useCancelSimSessionMutation } from '../../api/simulationApi';

type AdminRecentJob = NonNullable<ReturnType<typeof useGetSystemMetricsQuery>['data']>['recentJobs'][number];
type AdminStateChange = NonNullable<ReturnType<typeof useGetStateChangesQuery>['data']>['items'][number];
type AdminStateSnapshot = NonNullable<ReturnType<typeof useGetStateSnapshotsQuery>['data']>['items'][number];

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
};

const formatBytesMb = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const statusColor = (status: string) => {
  if (status === 'active') return 'green';
  if (status === 'paused') return 'gold';
  if (status === 'completed') return 'blue';
  if (status === 'cancelled') return 'red';
  return 'default';
};

const getSessionColumns = (
  opts: {
    onOpenFarm: (farmId: string) => void;
    onCancelSession: (sessionId: string) => Promise<void>;
    cancellingSessionId: string | null;
  }
): ColumnsType<AdminActiveSessionMetrics> => [
  {
    title: 'Сессия',
    dataIndex: 'id',
    width: 230,
    render: (value: string) => <span>{value.slice(0, 8)}…</span>,
  },
  {
    title: 'Ферма',
    dataIndex: 'farmName',
    render: (value: string, record) => value || record.farmId,
  },
  {
    title: 'Режим',
    dataIndex: 'mode',
    render: (mode: string) => <Tag>{mode}</Tag>,
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    render: (status: string) => <Tag color={statusColor(status)}>{status}</Tag>,
  },
  {
    title: 'Sim Date',
    dataIndex: 'simDate',
    render: (value?: string | null) => value || '—',
  },
  {
    title: 'Посадки',
    dataIndex: 'plantingsCount',
  },
  {
    title: 'CPV(sim)',
    dataIndex: 'cpvSimRows',
  },
  {
    title: 'State changes',
    dataIndex: 'stateChangesCount',
  },
  {
    title: 'Последний snapshot',
    dataIndex: 'lastSnapshotAt',
    render: (value?: string | null) => formatDateTime(value),
  },
  {
    title: 'Действия',
    key: 'actions',
    width: 190,
    render: (_, record) => (
      <Space>
        <Button size="small" onClick={() => opts.onOpenFarm(record.farmId)}>
          Открыть ферму
        </Button>
        <Popconfirm
          title="Отменить сессию?"
          description="Сессия будет остановлена"
          okText="Отменить сессию"
          cancelText="Назад"
          onConfirm={() => opts.onCancelSession(record.id)}
        >
          <Button
            size="small"
            danger
            loading={opts.cancellingSessionId === record.id}
          >
            Stop
          </Button>
        </Popconfirm>
      </Space>
    ),
  },
];

const jobColumns: ColumnsType<AdminRecentJob> = [
  {
    title: 'Job',
    dataIndex: 'id',
    width: 140,
    render: (value: string) => <span>{value.slice(0, 8)}…</span>,
  },
  {
    title: 'Ферма',
    dataIndex: 'farmName',
  },
  {
    title: 'Тип',
    dataIndex: 'jobType',
    render: (value: string) => <Tag>{value}</Tag>,
  },
  {
    title: 'Статус',
    dataIndex: 'status',
    render: (status: string) => <Tag color={statusColor(status)}>{status}</Tag>,
  },
  {
    title: 'Прогресс',
    render: (_, row) => `${row.processedItems}/${row.totalItems} (${row.failedItems} failed)`,
  },
  {
    title: 'Запрошен',
    dataIndex: 'requestedAt',
    render: (value: string) => formatDateTime(value),
  },
  {
    title: 'Завершён',
    dataIndex: 'finishedAt',
    render: (value?: string | null) => formatDateTime(value),
  },
];

const stateChangeColumns: ColumnsType<AdminStateChange> = [
  {
    title: 'Время',
    dataIndex: 'createdAt',
    width: 170,
    render: (value: string) => formatDateTime(value),
  },
  {
    title: 'Ферма',
    dataIndex: 'farmName',
    render: (value: string | null, row) => value || row.farmId,
  },
  {
    title: 'Session',
    dataIndex: 'simSessionId',
    render: (value: string | null) => (value ? `${value.slice(0, 8)}…` : '—'),
  },
  {
    title: 'Entity',
    render: (_, row) => (
      <Space direction="vertical" size={0}>
        <Tag>{row.entityType}</Tag>
        <span>{row.entityId.slice(0, 8)}…</span>
      </Space>
    ),
  },
  {
    title: 'Property',
    render: (_, row) => row.propertyName || row.propertyCode || row.propertyId,
  },
  {
    title: 'Value',
    dataIndex: 'resultingValue',
    ellipsis: true,
    render: (value: string) => (value && value.length > 120 ? `${value.slice(0, 120)}…` : value),
  },
  {
    title: 'Source',
    dataIndex: 'sourceType',
    render: (value: string) => <Tag>{value}</Tag>,
  },
];

const stateSnapshotColumns: ColumnsType<AdminStateSnapshot> = [
  {
    title: 'Время',
    dataIndex: 'createdAt',
    width: 170,
    render: (value: string) => formatDateTime(value),
  },
  {
    title: 'Ферма',
    dataIndex: 'farmName',
    render: (value: string | null, row) => value || row.farmId,
  },
  {
    title: 'Session',
    dataIndex: 'simSessionId',
    render: (value: string | null) => (value ? `${value.slice(0, 8)}…` : '—'),
  },
  {
    title: 'Entity',
    render: (_, row) => (
      <Space direction="vertical" size={0}>
        <Tag>{row.entityType}</Tag>
        <span>{row.entityId.slice(0, 8)}…</span>
      </Space>
    ),
  },
  {
    title: 'Reason',
    dataIndex: 'reason',
    render: (value: string) => <Tag>{value}</Tag>,
  },
  {
    title: 'Lane',
    dataIndex: 'lane',
    render: (value: string | null) => (value ? <Tag>{value}</Tag> : '—'),
  },
  {
    title: 'Sim Date',
    dataIndex: 'simDate',
    render: (value: string | null) => value || '—',
  },
  {
    title: 'Snapshot data',
    dataIndex: 'snapshotPreview',
    ellipsis: true,
    render: (value: string | null) => (value && value.length > 160 ? `${value.slice(0, 160)}…` : value || '—'),
  },
];

const AdminSystemMetricsPage: React.FC = () => {
  const navigate = useNavigate();
  const [cancellingSessionId, setCancellingSessionId] = useState<string | null>(null);
  const [cancelSimSession] = useCancelSimSessionMutation();

  const { data, isLoading, isFetching, isError, refetch } = useGetSystemMetricsQuery();
  const {
    data: stateChanges,
    isLoading: loadingStateChanges,
    isFetching: fetchingStateChanges,
    refetch: refetchStateChanges,
  } = useGetStateChangesQuery({ limit: 50 });
  const {
    data: stateSnapshots,
    isLoading: loadingStateSnapshots,
    isFetching: fetchingStateSnapshots,
    refetch: refetchStateSnapshots,
  } = useGetStateSnapshotsQuery({ limit: 50 });

  const overview = data?.overview;

  const cards = useMemo(
    () => [
      { title: 'Фермы', value: overview?.farmsCount ?? 0 },
      { title: 'Активные посадки', value: overview?.activePlantingsCount ?? 0 },
      { title: 'Сим-сессии (всего)', value: overview?.simSessionsTotal ?? 0 },
      { title: 'Сим-сессии (active)', value: overview?.simSessionsActive ?? 0 },
      { title: 'State changes (24ч)', value: overview?.stateChanges24h ?? 0 },
      { title: 'Snapshots (24ч)', value: overview?.stateSnapshots24h ?? 0 },
    ],
    [overview]
  );

  const jobCards = useMemo(
    () => [
      { title: 'Jobs total', value: data?.jobsOverview?.total ?? 0 },
      { title: 'Jobs pending', value: data?.jobsOverview?.pending ?? 0 },
      { title: 'Jobs running', value: data?.jobsOverview?.running ?? 0 },
      { title: 'Jobs failed', value: data?.jobsOverview?.failed ?? 0 },
    ],
    [data]
  );

  const handleOpenFarm = async (_farmId: string) => {
    navigate('/saves?type=free');
  };

  const handleCancelSession = async (sessionId: string) => {
    try {
      setCancellingSessionId(sessionId);
      await cancelSimSession(sessionId).unwrap();
      message.success('Сессия остановлена');
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || 'Не удалось остановить сессию');
    } finally {
      setCancellingSessionId(null);
    }
  };

  const sessionColumns = getSessionColumns({
    onOpenFarm: handleOpenFarm,
    onCancelSession: handleCancelSession,
    cancellingSessionId,
  });

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: 'Администрирование' },
            { title: 'Система (debug)' },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card
          title="Служебные метрики симуляции"
          extra={
            <Button onClick={() => refetch()} loading={isFetching}>
              Обновить
            </Button>
          }
        >
          <Spin spinning={isLoading}>
            {isError || !data ? (
              <Empty description="Не удалось загрузить метрики" />
            ) : (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row gutter={[12, 12]}>
                  {cards.map((item) => (
                    <Col key={item.title} xs={24} sm={12} md={8} lg={6}>
                      <Card>
                        <Statistic title={item.title} value={item.value} />
                      </Card>
                    </Col>
                  ))}
                </Row>

                <Card title="Процесс / Scheduler">
                  <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Срез данных">{formatDateTime(data.timestamp)}</Descriptions.Item>
                    <Descriptions.Item label="NODE_ENV">{data.process.nodeEnv}</Descriptions.Item>
                    <Descriptions.Item label="Uptime">{data.process.uptimeSeconds} сек</Descriptions.Item>
                    <Descriptions.Item label="Scheduler">
                      <Tag color={data.scheduler.enabled ? 'green' : 'red'}>
                        {data.scheduler.enabled ? 'enabled' : 'disabled'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Scheduler interval">
                      {data.scheduler.intervalMs ? `${data.scheduler.intervalMs} ms` : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Memory RSS">{formatBytesMb(data.process.memory.rss)}</Descriptions.Item>
                    <Descriptions.Item label="Heap Used">{formatBytesMb(data.process.memory.heapUsed)}</Descriptions.Item>
                    <Descriptions.Item label="Heap Total">{formatBytesMb(data.process.memory.heapTotal)}</Descriptions.Item>
                  </Descriptions>
                </Card>

                <Card title="Активные сим-сессии">
                  <Table
                    rowKey={(row) => row.id}
                    dataSource={data.activeSessions}
                    columns={sessionColumns}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: 'Активных сессий нет' }}
                    scroll={{ x: 1100 }}
                  />
                </Card>

                <Card title="Очередь simulation jobs">
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    {jobCards.map((item) => (
                      <Col key={item.title} xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic title={item.title} value={item.value} />
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <Table
                    rowKey={(row) => row.id}
                    dataSource={data.recentJobs}
                    columns={jobColumns}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: 'Задач пока нет' }}
                    scroll={{ x: 1000 }}
                  />
                </Card>

                <Card
                  title="Последние state_changes"
                  extra={
                    <Button onClick={() => refetchStateChanges()} loading={fetchingStateChanges}>
                      Обновить
                    </Button>
                  }
                >
                  <Spin spinning={loadingStateChanges}>
                    <Table
                      rowKey={(row) => row.id}
                      dataSource={stateChanges?.items || []}
                      columns={stateChangeColumns}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'Записей state_changes нет' }}
                      scroll={{ x: 1200 }}
                    />
                  </Spin>
                </Card>

                <Card
                  title="Последние state_snapshots"
                  extra={
                    <Button onClick={() => refetchStateSnapshots()} loading={fetchingStateSnapshots}>
                      Обновить
                    </Button>
                  }
                >
                  <Spin spinning={loadingStateSnapshots}>
                    <Table
                      rowKey={(row) => row.id}
                      dataSource={stateSnapshots?.items || []}
                      columns={stateSnapshotColumns}
                      pagination={{ pageSize: 10 }}
                      locale={{ emptyText: 'Записей state_snapshots нет' }}
                      scroll={{ x: 1200 }}
                    />
                  </Spin>
                </Card>
              </Space>
            )}
          </Spin>
        </Card>
      </Space>
    </MainLayout>
  );
};

export default AdminSystemMetricsPage;
